import {computed, makeObservable, observable, reaction, runInAction} from 'mobx'
import {getSettlementsSummaryData, SettlementsSummaryData} from '@/stores'
import {message} from 'antd'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'
import {injectable} from 'inversify'
import {LoadingState} from '@/types'
import {PaginationContainerStoreType, TRangePickerContainerStore, TSelectContainerStore} from '@/components'
import {DEFAULT_PAGE_SIZE, SERVER_DATE_FORMAT} from '@/constants'
import {ITransactionsHistoryDataStore} from '@/pages/Overview'

@injectable()
export class PaymentsTransactionsHistoryDataStore implements ITransactionsHistoryDataStore {
    page = 0
    size = DEFAULT_PAGE_SIZE
    _data: SettlementsSummaryData[] = []
    loadingState: LoadingState = LoadingState.IDLE
    rangePickerStore: TRangePickerContainerStore
    currencySelectStore: TSelectContainerStore
    paginationStore: PaginationContainerStoreType
    constructor(
        rangePickerStore: TRangePickerContainerStore,
        currencySelectStore: TSelectContainerStore,
        paginationStore: PaginationContainerStoreType
    ) {
        makeObservable(this, {
            page: observable,
            size: observable,
            loadingState: observable,
            isLoading: computed,
            data: computed
        })
        this.rangePickerStore = rangePickerStore
        this.currencySelectStore = currencySelectStore
        this.paginationStore = paginationStore
        this.loadData()

        reaction(
            () => ({
                from: this.rangePickerStore.startDate,
                to: this.rangePickerStore.endDate,
                currency: this.currencySelectStore.value,
                page: this.paginationStore.pageNumber,
                size: this.paginationStore.pageSize
            }),
            ({from, to, currency, page, size }) => {
                if (from && to && currency && page && size) {
                    this.loadData()
                }
            }
        )
    }

    get data() {
        return this._data
    }

    get isLoading(): boolean {
        return this.loadingState === LoadingState.LOADING
    }

    public loadData = async() => {
        const request = {
            from: this.rangePickerStore.startDate.format(SERVER_DATE_FORMAT),
            to: this.rangePickerStore.endDate.format(SERVER_DATE_FORMAT),
            currency: this.currencySelectStore.value,
            page: this.paginationStore.pageNumber,
            size: this.paginationStore.pageSize
        }

        if (this.loadingState === LoadingState.LOADING) {
            return
        }

        runInAction(() => this.loadingState = LoadingState.LOADING)

        try {
            const {result, error} = await getSettlementsSummaryData(request)
            if (error) throw new Error(error.message)
            const {data, totalCount} = result

            runInAction(() => this._data = data)
            this.paginationStore.setTotal(totalCount)

        } catch (error) {
            captureSentryError({
                name: 'PaymentsTransactionsHistoryDataStore loadData Error',
                description: getErrorMessage(error, 'Failed to load transactions history data'),
                fingerprint: ['payments-transactions-history-store-load-data'],
                error
            })
            message.error(error.message)
        } finally {
           runInAction(() => this.loadingState = LoadingState.DONE)
        }
    }
}
